/**
 * Shared geometry entity type vocabulary + low-level numeric helpers.
 *
 * Extracted from `simpleGeometry.ts` so `previewBuilders.ts` and
 * `extendedPropertyMutations.ts` can pull these primitives in without
 * re-importing the parent simpleGeometry module — breaks the
 * depcruise cycles
 *
 *   simpleGeometry.ts ↔ previewBuilders.ts
 *   simpleGeometry.ts ↔ extendedPropertyMutations.ts
 *   previewBuilders.ts → simpleGeometry.ts → extendedPropertyMutations.ts
 *
 * The parent `simpleGeometry.ts` re-exports every symbol so existing
 * import paths keep working.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { bulgeArc } from "@dxf-ai-editor/drawing-model";

export type SimpleGeometryPoint = {
  x: number;
  y: number;
};

export type SimpleGeometryPointLike = SimpleGeometryPoint & {
  z?: number;
};

export type SimpleLineEntity = {
  id: string;
  type: "Line";
  spaceId: string;
  start: SimpleGeometryPointLike;
  end: SimpleGeometryPointLike;
};

export type SimplePointEntity = {
  id: string;
  type: "Point";
  spaceId: string;
  location: SimpleGeometryPointLike;
  rotation: number;
};

export type SimpleCircleEntity = {
  id: string;
  type: "Circle";
  spaceId: string;
  center: SimpleGeometryPointLike;
  radius: number;
};

export type SimpleArcEntity = {
  id: string;
  type: "Arc";
  spaceId: string;
  center: SimpleGeometryPointLike;
  radius: number;
  startAngle: number;
  endAngle: number;
};

// ELLIPSE (incl. partial ellipse arc). Full ellipse has startParam=0
// and endParam=2π; any other combination is an ellipse arc.
export type SimpleEllipseEntity = {
  id: string;
  type: "Ellipse";
  spaceId: string;
  // SL-15 — center (DXF 10/20/30) + majorAxisEndpoint (11/21/31) are FULL 3D:
  // an elevated / out-of-XY-plane ellipse (e.g. exploded out of a tilted+
  // elevated INSERT) carries a real z. `SimpleGeometryPointLike` (z optional) so
  // the pervasive 2D `{x, y}` construction literals keep typechecking.
  center: SimpleGeometryPointLike;
  majorAxisEndpoint: SimpleGeometryPointLike; // vector relative to center; z tilts the plane
  ratio: number; // minor/major, positive (0, 1]
  startParam: number;
  endParam: number;
  // SL-15 — OCS extrusion normal (210/220/230). OMITTED when the WCS default
  // (0,0,1) so a planar ellipse round-trips byte-identical.
  extrusion?: { x: number; y: number; z: number };
};

// SPLINE — full-fidelity NURBS preview shape. Carries both fit-points
// and control-points modes; the renderer dispatches off whichever
// payload is present. Per CLAUDE.md ПРИОРИТЕТ #1 — cubic Bezier
// rendering only.
export type SimpleSplineEntity = {
  id: string;
  type: "Spline";
  spaceId: string;
  degree: number;
  closed: boolean;
  rational: boolean;
  fitPoints: SimpleGeometryPoint[];
  controlPoints: SimpleGeometryPoint[];
  knots: number[];
  weights: number[];
  startTangent?: SimpleGeometryPoint;
  endTangent?: SimpleGeometryPoint;
};

export type SimpleLwpolylineEntity = {
  id: string;
  type: "LWPOLYLINE";
  spaceId: string;
  vertices: {
    x: number;
    y: number;
    bulge?: number;
    startWidth?: number;
    endWidth?: number;
  }[];
  closed: boolean;
  // DXF group 70 bit 128 — plinegen (continuous linetype generation across
  // vertices). Optional, mirroring the drawing-model `lwpolylineEntitySchema`
  // (`plinegen: z.boolean().optional()` — absent ⇒ false). The create-* input
  // has no plinegen (a freshly drafted polyline is plinegen=false, AutoCAD
  // parity); the explode path forwards the block child's typed flag.
  plinegen?: boolean;
  constantWidth: number;
  elevation: number;
  thickness: number;
};

export type SimpleTextEntity = {
  id: string;
  type: "Text";
  spaceId: string;
  insertionPoint: SimpleGeometryPoint;
  alignmentPoint?: SimpleGeometryPoint;
  height: number;
  rotation: number;
  contents: string;
  styleName: string;
  widthFactor: number;
  obliqueAngle: number;
  hJustification: number;
  vJustification: number;
  thickness: number;
  /**
   * GitHub Codex review PR #558 round 2 P2 — DXF group 71 (generation
   * flags: backward/upside-down bits). Optional — every PRE-EXISTING
   * caller omits it (defaulting to the schema's own 0), so this is a
   * strictly additive field; `blockMutations.ts`'s EXPLODE arm populates
   * it from `resolveExplodedAttribPlacement`'s `generationFlags` so the
   * shell PREVIEW stays byte-consistent with the persisted document's
   * TextEntity (shell↔doc parity, matching this module's other explode
   * fields).
   */
  generationFlags?: number;
};

export type SimpleMtextEntity = {
  id: string;
  type: "MText";
  spaceId: string;
  insertionPoint: SimpleGeometryPoint;
  nominalHeight: number;
  referenceWidth: number;
  attachmentPoint: number;
  drawingDirection: number;
  contents: string;
  styleName: string;
  rotation: number;
  lineSpacingStyle: number;
  lineSpacingFactor: number;
  backgroundFill: boolean;
};

export type SimpleDimensionEntity = {
  id: string;
  type: "Dimension";
  spaceId: string;
  styleName: string;
  dimensionText: string;
  textHeight: number;
  extLine1Point: SimpleGeometryPoint;
  extLine2Point: SimpleGeometryPoint;
  dimensionLinePoint: SimpleGeometryPoint;
  measurement: number;
  linearAngle: number;
  dimensionType: number;
  obliqueAngle: number;
  thirdPoint?: { x: number; y: number; z: number };
  arcPoint?: { x: number; y: number; z: number };
  leaderLength: number;
  anonymousBlockName: string;
  textRotation: number;
};

export type SimpleMultileaderEntity = {
  id: string;
  type: "Multileader";
  spaceId: string;
  styleName: string;
  textContents: string;
  textHeight: number;
  arrowPoint: SimpleGeometryPoint;
  landingPoint: SimpleGeometryPoint;
  arrowheadSize: number;
};

export type SimpleRevisionCloudEntity = {
  id: string;
  type: "Revision Cloud";
  spaceId: string;
  revisionCloudStyle: string;
  approximateArcLength: number;
  // REVCLOUD-MODES — AutoCAD creation method ("Rectangular" | "Polygonal"
  // | "Object"; a committed Freehand path persists as "Polygonal" — see
  // the create-revcloud contract note in contracts/src/editor.ts).
  creationMode: string;
  // Cloud vertex chain for Polygonal / Freehand / Object clouds. For the
  // rectangular flow this stays undefined and corner1/corner2 carry the
  // shape; for the other modes corner1/corner2 hold the chain's AABB so
  // legacy corner-based consumers (preview grid, hit-test heuristics)
  // keep working.
  vertices?: SimpleGeometryPoint[];
  // Object mode — id of the closed source entity the cloud replaced.
  sourceEntityId?: string;
  corner1: SimpleGeometryPoint;
  corner2: SimpleGeometryPoint;
};

export type SimpleHatchEntity = {
  id: string;
  type: "Hatch";
  spaceId: string;
  patternName: string;
  patternScale: number;
  patternAngle: number;
  associative: boolean;
  hatchStyle: number;
  patternType: number;
};

export type SimpleBlockInsertEntity = {
  id: string;
  type: "Block";
  spaceId: string;
  blockName: string;
  insertionPoint: SimpleGeometryPoint;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
  rotation: number;
  columnCount: number;
  rowCount: number;
  columnSpacing: number;
  rowSpacing: number;
};

export type SimpleArrayRectEntity = {
  id: string;
  type: "Rectangular Associative Array";
  spaceId: string;
  basePoint: SimpleGeometryPoint;
  rowCount: number;
  columnCount: number;
  rowSpacing: number;
  columnSpacing: number;
  axisAngle: number;
  associative: boolean;
};

export type SimpleViewportEntity = {
  id: string;
  type: "Viewport";
  spaceId: string;
  center: SimpleGeometryPoint;
  width: number;
  height: number;
  displayLocked: boolean;
  scale: string;
  shadePlot: string;
  viewHeight: number;
  // Model-space view center (DXF group 12) — the model-space point that
  // appears at the geometric centre of the paper-space viewport.
  // AutoCAD parity: `MVIEW` defaults this to the model-space extent
  // centre when model geometry exists, and (0, 0) otherwise.
  viewCenterX: number;
  viewCenterY: number;
  // View twist angle (DXF group 51, degrees) — a created viewport has NO twist
  // (0). Optional so existing constructors need no change; the preview derivation
  // reads it with a `?? 0` default (matching the import factory's canonical form).
  viewTwistAngle?: number;
  layer: string;
};

export type ExtendedGeometryEntity =
  | SimpleSplineEntity
  | SimpleLwpolylineEntity
  | SimpleTextEntity
  | SimpleMtextEntity
  | SimpleDimensionEntity
  | SimpleMultileaderEntity
  | SimpleRevisionCloudEntity
  | SimpleHatchEntity
  | SimpleBlockInsertEntity
  | SimpleArrayRectEntity
  | SimpleViewportEntity;

export type SimpleGeometryEntity =
  | SimpleLineEntity
  | SimplePointEntity
  | SimpleCircleEntity
  | SimpleArcEntity;

export type AllGeometryEntity = SimpleGeometryEntity | ExtendedGeometryEntity;

export type ExtendedGeometryPatch =
  | { entityId: string; type: "LWPOLYLINE"; changes: Record<string, unknown> }
  | { entityId: string; type: "Text"; changes: Record<string, unknown> }
  | { entityId: string; type: "MText"; changes: Record<string, unknown> }
  | { entityId: string; type: "Dimension"; changes: Record<string, unknown> }
  | { entityId: string; type: "Multileader"; changes: Record<string, unknown> }
  | {
      entityId: string;
      type: "Revision Cloud";
      changes: Record<string, unknown>;
    }
  | { entityId: string; type: "Hatch"; changes: Record<string, unknown> }
  | { entityId: string; type: "Block"; changes: Record<string, unknown> }
  | {
      entityId: string;
      type: "Rectangular Associative Array";
      changes: Record<string, unknown>;
    }
  | { entityId: string; type: "Viewport"; changes: Record<string, unknown> };

export type SimpleGeometryPropertyChange = {
  name: string;
  value: string;
};

export type SimpleGeometryPropertySkip = {
  entityId: string;
  property: string;
  reason: string;
  /**
   * fix/properties-apply-derived-fields-skip — distinguishes a KNOWN
   * computed/display-only field (Line Length/Angle, Circle Diameter/
   * Circumference/Area, Arc Total angle/Arc length, LWPOLYLINE Length/Area,
   * Dimension Measurement, …: a `read-only-calc` typed descriptor — AutoCAD
   * shows these read-only in the Properties palette, never settable by
   * design) from a GENUINE incompatibility (the property does not apply to
   * this entity type at all, or fails a real validation rule). The
   * Properties panel resubmits every displayed row on every edit (not just
   * the touched one), so a derived row rides along UNCHANGED on every
   * apply — that is expected, not a failed write, and must never inflate
   * the user-facing "skipped N incompatible property application(s)"
   * count (`summarizePropertyMutation`, apps/web `reducerHelpers.ts`).
   */
  kind: "derived" | "incompatible";
};

export type SimpleGeometryPatch =
  | {
      entityId: string;
      type: "Line";
      changes: {
        // DXF LINE endpoints carry an optional Z (group 30/31). A 2-D grip /
        // Properties edit MUST keep the existing Z (the model field is
        // `drawingPointOptionalZSchema`); `patchEntityById` shallow-merges
        // `start` / `end`, so the patch must re-emit the full point incl. Z or
        // the merge would drop a non-zero elevation (GEOMETRY-EDIT-INVARIANTS
        // Z-preserve, residual R2 #317).
        start?: SimpleGeometryPointLike;
        end?: SimpleGeometryPointLike;
      };
    }
  | {
      entityId: string;
      type: "Circle";
      changes: {
        // DXF CIRCLE center carries an optional Z (group 30). A 2-D Properties /
        // grip edit MUST keep the existing Z (the model field is
        // `drawingPointOptionalZSchema`); `patchEntityById` shallow-merges
        // `center`, so the patch must re-emit the full point incl. Z or the merge
        // would drop a non-zero elevation (GEOMETRY-EDIT-INVARIANTS Z-preserve,
        // residual R2 #317 — mirror of the LINE start/end contract above).
        center?: SimpleGeometryPointLike;
        radius?: number;
      };
    }
  | {
      entityId: string;
      type: "Arc";
      changes: {
        // DXF ARC center carries an optional Z (group 30) — same Z-preserve
        // contract as CIRCLE above (residual R2 #317).
        center?: SimpleGeometryPointLike;
        radius?: number;
        startAngle?: number;
        endAngle?: number;
      };
    };

export type SimpleGeometryPropertyChangeResult = {
  entity: EntityPreview;
  patch: SimpleGeometryPatch | null;
  appliedChanges: SimpleGeometryPropertyChange[];
  skipped: SimpleGeometryPropertySkip[];
  summary: string;
};

// ── Low-level numeric helpers ──────────────────────────────────────────
//
// These live alongside the type vocabulary because both
// `previewBuilders.ts` and `simpleGeometry.ts` consume them. Keeping
// them here means neither module needs a back-import on the other.

export function formatNumber(value: number): string {
  const rounded = Number(value.toFixed(3));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export function parseNumber(
  value: string | undefined,
  fallback: number,
): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Parse the `x,y[,z];…` point-list PREVIEW WIRE FORM — the exact inverse of the
 * `formatNumber`-joined serialization every production preview builder emits for
 * `Vertices` / `Control points` / `Fit points`
 * (`previewBuilders.deriveLwpolylinePreviewProperties`,
 * `dxfGeometry.buildLwpolylinePreview` / `buildPolylinePreview` /
 * `buildSplinePreview`, and the typed descriptors' own `display`).
 *
 * `z` is `undefined` when the token was a 2-pair, so a merge can keep the
 * existing point's Z by index (3-D spline preserve).
 *
 * ALL-OR-NOTHING: returns `null` for a MALFORMED list — a pair with fewer than
 * 2 or more than 3 tokens, an EMPTY / whitespace token (P2 R5 #1 — `1,,2` /
 * `10,` must NOT coerce to 0), or any non-finite coordinate. A caller that
 * cannot trust half a vertex list must not consume half a vertex list: the
 * property-edit path turns `null` into an S15 no-op, and the MVIEW-FIT extent
 * path (`extent.ts`) lets the entity contribute nothing rather than fit the
 * viewport to a partially-decoded polyline.
 *
 * RELOCATED here from `descriptors/curves.ts` (its only caller until MVIEW FIT
 * needed the same decode): the SERIALIZE half of this wire form is
 * `formatNumber` directly above, so the PARSE half belongs in the same leaf
 * module rather than behind the descriptor registry — one truth for the format,
 * and `extent.ts` does not have to import descriptor machinery to read a bag.
 */
export function parsePointListProperty(
  serialized: string,
): { x: number; y: number; z?: number }[] | null {
  const out: { x: number; y: number; z?: number }[] = [];
  for (const pair of serialized.split(";")) {
    if (pair.trim() === "") continue;
    const parts = pair.split(",");
    if (parts.length < 2 || parts.length > 3) return null;
    if (parts.some((p) => p.trim() === "")) return null; // empty token reject
    const nums = parts.map(Number);
    if (nums.some((n) => !Number.isFinite(n))) return null;
    out.push(
      parts.length === 3
        ? { x: nums[0]!, y: nums[1]!, z: nums[2]! }
        : { x: nums[0]!, y: nums[1]! },
    );
  }
  return out;
}

/**
 * Parse the `Closed` preview-bag row (DXF group 70 bit 1 for both polyline
 * families) into the boolean every geometry walk needs.
 *
 * TOLERANT BY NECESSITY, not by taste — this repo has TWO live producers of
 * the row and they do not agree:
 *
 *   - the import-path builders (`apps/web/.../state/dxfGeometry.ts`'s
 *     `buildLwpolylinePreview` / `buildPolylinePreview`) emit the canonical
 *     `"true"` / `"false"`;
 *   - the command-engine factory (`./previewBuilders.ts`'s
 *     `deriveLwpolylinePreviewProperties`) still emits `"Yes"` / `"No"`, and
 *     older serialized state carries `"1"` / `"0"`.
 *
 * Reading only the canonical form would silently drop the CLOSING span of
 * every command-engine-CREATED polyline — the exact defect
 * `dxfGeometry.buildLwpolylinePreview`'s own comment records having had
 * ("Writing Yes/No here used to leave `closed=false`"). So all three spellings
 * are accepted, case- and whitespace-insensitively, and anything else is
 * `false` (an ABSENT row means an open polyline).
 *
 * ONE TRUTH, placed beside {@link parsePointListProperty} for the same reason
 * that one is here: this is the PARSE half of a preview wire form whose
 * serialize half lives in the builders, and a leaf module is reachable from
 * every consumer without dragging the descriptor registry in.
 *
 * KNOWN REMAINING COPIES, named rather than left to be rediscovered: three
 * `apps/web` sites still inline this two-line parse —
 * `scene/entityBounds.ts`, `scene/gripCalculation.ts` and
 * `scene3d/adapters/lwpolylineToRenderable.ts` (`scene/draftSharedHelpers.ts`
 * spells the same rule as a regex). They are behaviour-identical to this
 * function; rewiring them is a cross-package edit in files owned by other
 * work in flight, so it is a follow-up rather than a silent widening of this
 * change.
 */
export function parseClosedFlagProperty(raw: string | undefined): boolean {
  // An ABSENT row is answered on its own line rather than through a `?? ""`
  // default. The default was UNOBSERVABLE — every string but the three
  // accepted spellings returns `false`, so no input could distinguish `""`
  // from any other placeholder, which made it an equivalent mutant the
  // targeted mutation gate reported and no test could ever kill. Written this
  // way the absent case is a branch a test DOES discriminate (`undefined` vs
  // `"true"`), and `raw.trim()` below would throw on `undefined` if the guard
  // were removed, so its own mutants die too.
  if (raw === undefined) return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
}

// Degree span of an arc from start→end (CCW), with wraparound + the full-circle
// special case. ONE TRUTH the typed property descriptors reuse for the Arc
// "Total angle" / "Arc length" display (mirrors the production single-select
// builder dxfGeometry.buildArcPreview) — no re-implementation. RELOCATED here
// from `simpleGeometry.ts` (which re-exports it for back-compat): the
// `descriptors/simpleGeometry.ts` import of the barrel closed a runtime module
// cycle once `extendedPropertyMutations` gained its typed-writer mirror edge
// (S15 Slice A @codex round on c8394b70) — the barrel `export *`s the shell
// applier, so descriptor tables were built against a partially-initialized
// registry and EVERY Line/Circle/Arc edit rejected. Leaf placement follows the
// same cycle-break pattern as formatNumber/parseNumber above.
export function normalizeAngleSpan(
  startAngle: number,
  endAngle: number,
): number {
  const span = (((endAngle - startAngle) % 360) + 360) % 360;
  return span === 0 && startAngle !== endAngle ? 360 : span;
}

// Common preview properties surfaced on every entity (Layer, Color,
// Linetype, Lineweight, Thickness). Exported so `pickCommonProperties`
// below and the per-type derivers in `previewBuilders.ts` agree on the
// list without one having to import from the other.
export const simpleGeometryCommonPreviewProperties = [
  "Layer",
  "Color",
  "Linetype",
  "Lineweight",
  "Thickness",
] as const;

export function pickCommonProperties(
  properties: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const propertyName of simpleGeometryCommonPreviewProperties) {
    const value = properties[propertyName];

    if (typeof value === "string") {
      result[propertyName] = value;
    }
  }

  return result;
}

/* ── Computed property calculators ─────────────────── */

/**
 * One vertex of a path being measured: where it is, and — for the polyline
 * families that have them — the DXF group-42 `bulge` of the span LEAVING it.
 *
 * Both extras are optional so that every existing caller's vertex shape still
 * satisfies it: a list without `z` is planar and a list without `bulge` is
 * straight, which is what the two helpers below already assumed.
 */
export type PolylineMetricVertex = {
  x: number;
  y: number;
  z?: number | undefined;
  /** Bulge of the span from THIS vertex to the next (DXF group 42). */
  bulge?: number | undefined;
};

/** One drawn span of a path: its two ends and the bulge that bows it. */
interface PolylineMetricSpan {
  readonly from: PolylineMetricVertex;
  readonly to: PolylineMetricVertex;
  readonly bulge: number;
}

/**
 * The spans a vertex list actually DRAWS, in order — the single walk both
 * metrics below iterate, so Length and Area can never disagree about which
 * spans exist.
 *
 * `n - 1` spans open, `n` closed (the last wrapping back to the first), and
 * NONE below two vertices. This is the renderer's own count verbatim
 * (`cad-core/polylineWidthBand.ts`'s `polylineSpanSubSegments`:
 * `closed ? points.length : points.length - 1`), which is the point: the
 * palette must measure the curve the canvas draws.
 */
function pathSpans(
  vertices: readonly PolylineMetricVertex[],
  closed: boolean,
): PolylineMetricSpan[] {
  const spans: PolylineMetricSpan[] = [];
  // Equivalent mutant: below two vertices there are no drawn spans to differ
  // about. The loop's own `closed ? n : n - 1` count is already 0 for n = 1
  // and -1 for n = 0, so removing this guard yields the SAME empty result for
  // every reachable input. Verified empirically over 16 degenerate cases
  // (n in {0,1} x 4 bulges x open/closed), guarded vs guard-removed: 0 differ.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: equivalent — see the proof above
  if (vertices.length < 2) return spans;
  const count = closed ? vertices.length : vertices.length - 1;
  for (let i = 0; i < count; i++) {
    const from = vertices[i]!;
    spans.push({
      from,
      to: vertices[(i + 1) % vertices.length]!,
      // A missing group 42 is a STRAIGHT span. `?? 0` is mandatory rather than
      // cosmetic: `bulgeArc`'s straight-span guard is `Math.abs(bulge) < EPS`
      // and `Math.abs(undefined)` is `NaN`, which fails that test and would
      // poison the whole span (the trap `heavyPolylineRuns` documents too).
      bulge: from.bulge ?? 0,
    });
  }
  return spans;
}

/**
 * Chord path length of a polyline vertex list — the ONE length truth every
 * polyline family uses (LWPOLYLINE, the legacy heavy POLYLINE, the create-path
 * preview factory and the apps/web import-path preview builder all call THIS).
 *
 * DIMENSIONALITY (@codex F2, P2) — a vertex may carry an OPTIONAL `z`, and the
 * segment length is then the full XYZ distance. A vertex list WITHOUT `z`
 * (LWPOLYLINE's `{x, y, bulge, …}` vertices; any caller that projected to the
 * plane deliberately) measures exactly as before, by construction: the missing
 * component contributes `0 - 0`. This is a PARAMETERISATION of the one helper,
 * not a second length: a 3D POLYLINE (DXF group 70 bit 8) whose segments differ
 * in Z is understated — and a purely vertical segment reads as ZERO length —
 * when its Z is dropped, which is what the heavy-polyline descriptor did before
 * it started passing the Z through.
 *
 * BULGE (@codex PR #785 round 13, FINDING B — "Account for bulges in
 * heavy-polyline path metrics") — a span whose STARTING vertex carries a
 * nonzero group-42 bulge is a circular ARC, and this walk now measures the arc
 * instead of its chord. It is the family-wide change the previous revision of
 * this doc said the fix had to be, made HERE in the one helper rather than in
 * any single family's descriptor: LWPOLYLINE's Length row
 * (`descriptors/curves.ts`), its preview bag (`previewBuilders.ts`) and the
 * legacy heavy POLYLINE's row (`heavyPolylinePathMetrics.ts`) all call this
 * function, so all three became arc-aware together and no second length truth
 * exists. An open bulge-1 span from `(0, 0)` to `(10, 0)` is the half-circle
 * of radius 5 the renderer draws and measures `5π ≈ 15.708`, not the chord
 * `10` the palette used to print — a 57 % under-report on an ordinary shape.
 *
 * The arc itself is NOT re-derived here: {@link bulgeArc}
 * (`@dxf-ai-editor/drawing-model`) is the repo's one bulge→arc primitive, the
 * same one the renderer's span walk (`cad-core/polylineWidthBand.ts`'s
 * `polylineSpanSubSegments`, behind `polylineCentrelineSamples`) and the model
 * bounds (`drawing-model/wcsBoundsLeaf.ts`) read, and its `length` field is the
 * EXACT `R·|θ|` — no sampling, no tolerance. Measuring a sampled polyline
 * instead would under-report by `Rθ − 2NR·sin(θ/2N)` for N samples.
 *
 * A bulged span is PLANAR by construction — group 42 is a 2D-polyline field
 * (Autodesk DXF reference; `heavyPolylineRuns` zeroes the channel for every
 * other subtype) — so its arc length is the whole span length and the XYZ
 * chord formula applies only to the straight spans, which is where the Z of
 * the paragraph above still does its work.
 *
 * SPAN WALK — the spans are the ones the drawing actually has: `n - 1` open,
 * `n` closed, for `n ≥ 2`. The closed walk now includes `n = 2`, which the
 * old `length > 2` guard dropped. That guard silently halved the commonest
 * bulged shape there is: a full CIRCLE stored as a closed two-vertex polyline
 * with `bulge = 1` on both vertices (what DONUT and an exploded CIRCLE write)
 * has TWO drawn semicircles, and the renderer's own span count
 * (`closed ? points.length : points.length - 1`) has always drawn both.
 */
export function computePolylineLength(
  vertices: PolylineMetricVertex[],
  closed: boolean,
): number {
  let length = 0;
  for (const span of pathSpans(vertices, closed)) {
    // The ARC when the span bows, its chord when it does not. `bulgeArc`
    // returns `null` for `|bulge| ~ 0` and for coincident endpoints, which is
    // exactly the set of spans that have no arc to measure, so the fallback is
    // the same XYZ chord this walk has always taken (see the doc above on why
    // only a straight span can differ in Z).
    const arc = bulgeArc(span.from, span.to, span.bulge);
    if (arc) {
      length += arc.length;
      continue;
    }
    length += Math.hypot(
      span.to.x - span.from.x,
      span.to.y - span.from.y,
      (span.to.z ?? 0) - (span.from.z ?? 0),
    );
  }
  return length;
}

/**
 * Enclosed area of a CLOSED polyline — the shoelace of its vertices PLUS the
 * signed circular segment each bulged span cuts off or adds.
 *
 * The shoelace alone measures the CHORD polygon, so before @codex PR #785
 * round 13 every arc's bow was simply missing: a square with one side bowed
 * outward reported the square. The correction per span is the exact signed area
 * between the arc and its chord,
 *
 *     A = R²·(Δ − sin Δ) / 2,   Δ = the arc's SIGNED sweep about its centre,
 *
 * derived as "circular sector minus the centre triangle" (sector `R²Δ/2`,
 * triangle `R²·sin(Δ)/2`) and taken straight from {@link bulgeArc}'s
 * `signedSweep`, so the SIGN follows the bulge direction with nothing to get
 * backwards here: a span bowing INTO a counter-clockwise path's interior has
 * `Δ < 0`, contributes a negative area and SUBTRACTS, and one bowing outward
 * adds. Both are summed into the SIGNED shoelace and only the total is made
 * absolute — taking `|shoelace|` first and adding magnitudes would turn every
 * inward bow into an increase.
 *
 * Reference cases: a full circle stored as a closed two-vertex `bulge = 1`
 * polyline of radius 1 measures `π` (shoelace 0, two `−π/2` segments); the
 * 10×10 square with one side bowed outward by `bulge = −1` measures
 * `100 + 12.5π`.
 *
 * An OPEN path has no enclosed area and reports `0` — a measured "encloses
 * nothing", which callers must not confuse with the EMPTY row a subtype with
 * no defined area gets (`heavyPolylinePathMetrics.ts`).
 */
export function computePolylineArea(
  vertices: PolylineMetricVertex[],
  closed: boolean,
): number {
  // Two vertices CAN bound an area once they bow: the closed two-vertex
  // `bulge = 1` pair is a circle. Only a single vertex (or none) cannot.
  //
  // The `vertices.length < 2` half is an equivalent mutant: a single vertex's
  // closed shoelace is `x*y - x*y = 0` and its one degenerate span subtends no
  // arc, so the fall-through already returns 0. (The `!closed` half is NOT
  // equivalent and is killed by the open-path tests.) Verified over the same
  // 16 degenerate cases: 0 differing.
  // Stryker disable next-line ConditionalExpression,EqualityOperator: the length half is equivalent — see the proof above
  if (!closed || vertices.length < 2) {
    return 0;
  }

  let area = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    const vi = vertices[i]!;
    const vj = vertices[j]!;
    area += vi.x * vj.y;
    area -= vj.x * vi.y;
  }
  // SIGNED so far (counter-clockwise positive), because the segment
  // corrections below are signed too and must be able to cancel it.
  let signed = area / 2;

  for (const span of pathSpans(vertices, true)) {
    const arc = bulgeArc(span.from, span.to, span.bulge);
    if (!arc) continue;
    const sweep = arc.signedSweep;
    signed += (arc.radius * arc.radius * (sweep - Math.sin(sweep))) / 2;
  }

  return Math.abs(signed);
}
